import {FOOTER_SECTIONS, FooterLink} from './utils'
import {useTranslation} from 'react-i18next'
import {LanguagesShowcase} from './LanguagesShowcase'
import {TWebsiteLink} from '~/features/TLink/TWebsiteLink'
import {TAppLink} from '~/features/TLink/TAppLink'
import {TSupportLink} from '~/features/TLink/TSupportLink'
import {LaraLogoLink} from '~/features/LaraLogoLink'
import {InstagramIllustration, LinkedinIllustration, YoutubeIllustration} from './Illustrations'
import {LanguageMenu} from '~/features/LanguageMenu'
import {Button, ChevronDownIcon} from '@translated/babele-react'
import {applicationLanguages} from '~/utils/locales'
import {useCallback} from 'react'

const linkClassName = 'w-fit hyphens-auto transition-colors hover:text-primary-500 hover:underline'

export const Footer = () => {
  const {t, i18n} = useTranslation()

  const getLinkComponent = useCallback(
    (link: FooterLink) => {
      const label = t(link.label)

      switch (link.type) {
        case 'internal':
          return link.isAppUrl ? (
            <TAppLink key={link.label} to={link.url} className={linkClassName}>
              {label}
            </TAppLink>
          ) : (
            <TWebsiteLink key={link.label} to={link.url} className={linkClassName}>
              {label}
            </TWebsiteLink>
          )
        case 'external':
          return (
            <a key={link.label} href={link.url} target="_blank" className={linkClassName}>
              {label}
            </a>
          )
        case 'support':
          return (
            <TSupportLink key={link.label} to={link.url} className={linkClassName}>
              {label}
            </TSupportLink>
          )
      }
    },
    [t]
  )

  return (
    <section id="footer" className="relative [&>*]:px-6 [&>*]:py-10 [&>*]:sm:px-10">
      <LanguagesShowcase />
      <footer className="flex justify-center bg-[#4438CC] text-neutral-25">
        <div className="flex w-full max-w-7xl flex-col gap-10 md:gap-6">
          <section className="flex grid-cols-4 flex-col gap-6 sm:grid lg:gap-12">
            {FOOTER_SECTIONS.map(section => (
              <div key={section.title} className="flex flex-1 flex-col gap-4 font-medium sm:gap-6">
                <h1 className="hyphens-auto text-display-xs md:text-display-sm">
                  {t(section.title)}
                </h1>
                <nav className="flex flex-col gap-2 text-sm md:gap-3">
                  {section.links.map(link => getLinkComponent(link))}
                </nav>
              </div>
            ))}
          </section>
          <section className="flex flex-col justify-center gap-10 md:gap-6">
            <div className="flex w-full flex-col items-start justify-between gap-8 sm:flex-row sm:items-center sm:gap-5 md:my-0">
              <LanguageMenu>
                <Button
                  variant={{mode: 'ghost'}}
                  aria-label="Change language"
                  className="cursor-pointer rounded-sm px-4 py-2.5 text-sm font-medium text-neutral-25 ring-1 ring-neutral-25/2 transition-all hover:ring-1 hover:ring-neutral-25/3 focus-visible:outline-0 focus-visible:ring-1 focus-visible:ring-neutral-25/3"
                >
                  {applicationLanguages.find(language => language.code === i18n.language)?.label}
                  <ChevronDownIcon className="fill-neutral-25" />
                </Button>
              </LanguageMenu>
              <img
                width={400}
                height={67}
                src="/images/lara-certifications.webp"
                alt={t('footer.certifications')}
                sizes="auto, (max-width: 70rem) 100vw, 70rem"
              />
            </div>
            <div
              data-theme="translated-light"
              className="flex flex-col justify-between gap-6 lg:flex-row"
            >
              <LaraLogoLink />
              <div className="flex flex-col items-start gap-5 text-sm sm:flex-row sm:items-center md:justify-center lg:justify-end">
                <p>{t('copyright')}</p>
                <div>{t('footer.vat')} IT07173521001</div>
              </div>
              <div className="me-16 flex items-center gap-1 sm:me-12 md:justify-end 2xl:me-0">
                <a
                  title="LinkedIn"
                  href="https://www.linkedin.com/company/lara-translate"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <LinkedinIllustration className="scale-75" aria-hidden="true" />
                  <span className="sr-only">LinkedIn</span>
                </a>
                <a
                  title="YouTube"
                  href="https://www.youtube.com/@laratranslate"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <YoutubeIllustration className="scale-75" aria-hidden="true" />
                  <span className="sr-only">YouTube</span>
                </a>
                <a
                  title="Instagram"
                  href="https://www.instagram.com/laratranslate"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <InstagramIllustration className="scale-75" aria-hidden="true" />
                  <span className="sr-only">Instagram</span>
                </a>
              </div>
            </div>
          </section>
        </div>
      </footer>
    </section>
  )
}
